import { Injectable } from '@angular/core';

declare const $: any;

// Type alias for selector parameter to avoid repetition
type SelectorType = string | Element | NodeList;

export interface ScrollbarOptions {
  wheelPropagation?: boolean;
  suppressScrollX?: boolean;
  suppressScrollY?: boolean;
  wheelSpeed?: number;
  minScrollbarLength?: number;
}

@Injectable({
  providedIn: 'root'
})
export class PerfectScrollbarService {

  private readonly defaultOptions: ScrollbarOptions = {
    wheelPropagation: false,
    suppressScrollX: false,
    suppressScrollY: false,
    wheelSpeed: 1,
    minScrollbarLength: 20
  };

  /**
   * Initialize PerfectScrollbar on elements with specified selector
   * @param selector CSS selector or jQuery element
   * @param options Scrollbar configuration options
   */
  initialize(selector: SelectorType, options?: ScrollbarOptions): void {
    if (!this.isWindowsOS() || !this.isJQueryAvailable()) {
      return;
    }

    const mergedOptions = { ...this.defaultOptions, ...options };
    const $element = typeof selector === 'string' ? $(selector) : selector;

    if ($element?.length) {
      // Destroy existing scrollbar if any
      this.destroy($element);
      
      // Initialize new scrollbar
      $element.perfectScrollbar(mergedOptions);
    }
  }

  /**
   * Initialize PerfectScrollbar on elements with 'scrollable' class
   * @param options Scrollbar configuration options
   */
  initializeScrollableElements(options?: ScrollbarOptions): void {
    this.initialize('.scrollable', options);
  }

  /**
   * Update scrollbar for specified selector
   * @param selector CSS selector or jQuery element
   */
  update(selector: SelectorType): void {
    if (!this.isWindowsOS() || !this.isJQueryAvailable()) {
      return;
    }

    const $element = typeof selector === 'string' ? $(selector) : selector;
    
    if ($element?.length) {
      try {
        $element.perfectScrollbar('update');
      } catch (error) {
        // Silently ignore update errors
        console.debug('PerfectScrollbar update failed:', error);
      }
    }
  }

  /**
   * Destroy scrollbar for specified selector
   * @param selector CSS selector or jQuery element
   */
  destroy(selector: SelectorType): void {
    if (!this.isWindowsOS() || !this.isJQueryAvailable()) {
      return;
    }

    const $element = typeof selector === 'string' ? $(selector) : selector;
    
    if ($element?.length) {
      try {
        $element.perfectScrollbar('destroy');
      } catch (error) {
        // Scrollbar not initialized, which is expected when destroying non-existent instances
        console.debug('PerfectScrollbar destroy failed - element may not be initialized:', error);
      }
    }
  }

  /**
   * Initialize scrollbar with delay (useful for dynamic content)
   * @param selector CSS selector or jQuery element
   * @param options Scrollbar configuration options
   * @param delay Delay in milliseconds (default: 100ms)
   */
  initializeWithDelay(selector: SelectorType, options?: ScrollbarOptions, delay = 0): void {
    setTimeout(() => {
      this.initialize(selector, options);
    }, delay);
  }

  /**
   * Initialize scrollbar for main panel
   */
  initializeMainPanel(): void {
    this.initialize('.main-panel', {
      wheelPropagation: false,
      suppressScrollX: true,
      suppressScrollY: false
    });
  }

  /**
   * Initialize scrollbar for scrollable lists (with longer delay for dynamic content)
   * @param selectors Array of CSS selectors to initialize
   */
  initializeScrollableList(selectors: string[] = ['.scrollable-list', '.recursive-menu-scrollable-list']): void {
    for (const selector of selectors) {
      this.initializeWithDelay(selector, this.defaultOptions);
    }
  }

  /**
   * Initialize scrollbar for modal dialogs
   */
  initializeModalDialog(selector: string = '.modal-dialog'): void {
    this.initializeWithDelay(selector, this.defaultOptions);
  }

  /**
   * Initialize scrollbar for ag-grid components (both x and y axes)
   */
  initializeAgGrid(selector: string = '.ag-layout-auto-height'): void {
    this.initializeWithDelay(selector, this.defaultOptions);
    this.initializeWithDelay('.ag-body-horizontal-scroll-viewport, .ag-horizontal-left-spacer, .ag-horizontal-right-spacer', {
      wheelPropagation: false,
      suppressScrollX: false,
      suppressScrollY: true
    })
  }

  /**
   * Update all scrollbars in the application
   */
  updateAll(): void {
    if (!this.isWindowsOS() || !this.isJQueryAvailable()) {
      return;
    }

    // Update common scrollable elements
    this.update('.main-panel');
    this.update('.scrollable');
    this.update('.scrollable-list');
    this.update('.modal-dialog');
    this.update('.ag-grid-container');
  }

  /**
   * Check if current OS is Windows
   */
  isWindowsOS(): boolean {
    if (typeof navigator === 'undefined') {
      return false;
    }

    // Use modern userAgentData API if available
    const nav = navigator as any;
    if ('userAgentData' in navigator && nav.userAgentData) {
      return nav.userAgentData.platform === 'Windows';
    }

    // Fallback to userAgent string parsing
    return /Windows/.test(navigator.userAgent);
  }

  /**
   * Check if jQuery is available
   */
  private isJQueryAvailable(): boolean {
    return typeof window !== 'undefined' && Boolean((window as any).$);
  }
}
